import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Star, Crown, Zap, Mail, Calendar, BookOpen, Briefcase, Users } from 'lucide-react';
import ServiceOrderForm from './ServiceOrderForm';

const PricingPage = ({ onBusinessApplication }) => {
  const [selectedCategory, setSelectedCategory] = useState('directory');
  const [showServiceOrder, setShowServiceOrder] = useState(false);
  const [selectedServicePlan, setSelectedServicePlan] = useState(null);

  const directoryPlans = [
    {
      id: 'standard',
      name: 'Standard',
      setup: '£35.00',
      price: '£13',
      period: '/month',
      description: 'Perfect for small businesses getting started',
      icon: <Users className="h-6 w-6" />,
      color: 'bg-blue-500',
      features: [
        'AI Chatbot with offers',
        'Unique business URL',
        'Window stickers included',
        'Basic business profile',
        'QR code discount system',
        'Contact information display'
      ],
      limitations: ['No monthly updates included'],
      popular: false
    },
    {
      id: 'deluxe',
      name: 'Deluxe',
      setup: '£35.00',
      price: '£15',
      period: '/month',
      description: 'Enhanced features with regular updates',
      icon: <Star className="h-6 w-6" />,
      color: 'bg-primary',
      features: [
        'Everything in Standard',
        '3 updates per month',
        'Enhanced business profile',
        'Priority customer support',
        'Advanced analytics',
        'Social media integration'
      ],
      limitations: [],
      popular: true
    },
    {
      id: 'platinum',
      name: 'Platinum',
      setup: '£35.00',
      price: '£35',
      period: '/month',
      description: 'Premium package with video content',
      icon: <Crown className="h-6 w-6" />,
      color: 'bg-amber-500',
      features: [
        'Everything in Deluxe',
        'Bespoke promotional video',
        '6 updates per month',
        'Premium listing placement',
        'Featured in newsletters',
        'Dedicated account manager'
      ],
      limitations: [],
      popular: false
    },
    {
      id: 'elite',
      name: 'Elite/Sponsor',
      setup: 'Custom',
      price: 'Request',
      period: 'Quote',
      description: 'Tailored solutions for large enterprises',
      icon: <Zap className="h-6 w-6" />,
      color: 'bg-purple-600',
      features: [
        'Everything in Platinum',
        'Custom integration solutions',
        'Unlimited updates',
        'Homepage banner placement',
        'Exclusive partnership benefits',
        'Custom development'
      ],
      limitations: [],
      popular: false,
      isEnterprise: true
    }
  ];

  const jobPlans = [
    {
      id: 'basic-job',
      name: 'Basic Listing',
      price: '£15',
      period: '/month',
      description: 'Single job posting solution',
      icon: <Briefcase className="h-4 w-4" />,
      features: [
        'Post 1 job per month',
        '30-day listing duration',
        'Basic job description',
        'Contact form integration',
        'Search visibility'
      ],
      jobCount: '1 Job'
    },
    {
      id: 'premium-job',
      name: 'Premium Job Post',
      price: '£45',
      period: '/month',
      description: 'Multiple job postings with enhanced features',
      icon: <Briefcase className="h-4 w-4" />,
      features: [
        'Post up to 3 jobs',
        '60-day listing duration',
        'Enhanced job descriptions',
        'Logo and company branding',
        'Featured placement',
        'Application tracking'
      ],
      jobCount: '3 Jobs',
      popular: true
    },
    {
      id: 'job-bundle',
      name: 'Job Bundle',
      price: '£150',
      period: '/month',
      description: 'High-volume recruitment solution',
      icon: <Briefcase className="h-4 w-4" />,
      features: [
        'Post up to 10 jobs',
        '90-day listing duration',
        'Priority job placement',
        'Dedicated recruiter support',
        'Advanced filtering options',
        'Bulk job management'
      ],
      jobCount: '10 Jobs'
    },
    {
      id: 'elite-recruiter',
      name: 'Elite Recruiter',
      price: '£250',
      period: '/month',
      description: 'Enterprise-level recruitment platform',
      icon: <Briefcase className="h-4 w-4" />,
      features: [
        'Post up to 24 jobs',
        'Unlimited listing duration',
        'Premium brand placement',
        'Dedicated account manager',
        'Custom recruitment pages',
        'Advanced analytics dashboard'
      ],
      jobCount: '24 Jobs'
    }
  ];

  const eventPlans = [
    {
      id: 'free-event',
      name: 'Free Text Listing',
      price: 'Free',
      period: '',
      description: 'For charity and community events',
      icon: <Calendar className="h-4 w-4" />,
      features: [
        'Basic text listing',
        'Event date and time',
        'Location information',
        'Contact details',
        'Community visibility'
      ],
      eligibility: 'Charity or Community Events Only'
    },
    {
      id: 'standard-event',
      name: 'Standard Event',
      price: '£25',
      period: '/event',
      description: 'Professional event promotion',
      icon: <Calendar className="h-4 w-4" />,
      features: [
        'Enhanced event listing',
        'Event image upload',
        'Detailed description',
        'Registration links',
        'Social media sharing',
        '7-day promotion'
      ]
    },
    {
      id: 'featured-event',
      name: 'Featured Event',
      price: '£75',
      period: '/event',
      description: 'Premium event visibility',
      icon: <Calendar className="h-4 w-4" />,
      features: [
        'Everything in Standard',
        'Homepage featured placement',
        'Newsletter inclusion',
        'Extended 14-day promotion',
        'Priority search results',
        'Dedicated event page'
      ],
      popular: true
    },
    {
      id: 'seasonal-package',
      name: 'Seasonal Package',
      price: '£150',
      period: '/month',
      description: 'Regular event promotion solution',
      icon: <Calendar className="h-4 w-4" />,
      features: [
        'Up to 6 events per month',
        'All featured event benefits',
        'Recurring event support',
        'Brand consistency',
        'Monthly analytics report',
        'Dedicated support'
      ]
    }
  ];

  const blogPlans = [
    {
      id: 'basic-blog',
      name: 'Basic Blog Package',
      price: '£75',
      period: '/month',
      description: 'Entry-level content marketing',
      icon: <BookOpen className="h-4 w-4" />,
      features: [
        'Sidebar advertisement placement',
        'Brand mentions in articles',
        'Basic content integration',
        'Monthly performance report'
      ]
    },
    {
      id: 'premium-blog',
      name: 'Premium Blog Package',
      price: '£200',
      period: '/month',
      description: 'Enhanced content marketing solution',
      icon: <BookOpen className="h-4 w-4" />,
      features: [
        'Sponsored post creation',
        'Seasonal feature articles',
        'Enhanced brand visibility',
        'Social media promotion',
        'Detailed analytics',
        'Content strategy consultation'
      ],
      popular: true
    },
    {
      id: 'elite-blog',
      name: 'Elite Blog Package',
      price: '£500',
      period: '/month',
      description: 'Premium content marketing suite',
      icon: <BookOpen className="h-4 w-4" />,
      features: [
        'Exclusive feature articles',
        'Homepage banner placement',
        'Newsletter top spot',
        'Custom content creation',
        'Video content integration',
        'Dedicated content manager',
        'Comprehensive analytics suite'
      ]
    }
  ];

  const categories = [
    { id: 'directory', name: 'Directory Listings', icon: <Users className="h-5 w-5" />, plans: directoryPlans },
    { id: 'jobs', name: 'Job Postings', icon: <Briefcase className="h-5 w-5" />, plans: jobPlans },
    { id: 'events', name: "What's On Events", icon: <Calendar className="h-5 w-5" />, plans: eventPlans },
    { id: 'blog', name: 'Blog Packages', icon: <BookOpen className="h-5 w-5" />, plans: blogPlans }
  ];

  const handleGetStarted = (planId, category) => {
    if (category === 'directory') {
      // Directory listings go to Retailer Onboarding Flow
      if (onBusinessApplication) {
        onBusinessApplication(planId); // Pass just the package ID for retailer onboarding
      }
    } else {
      // Other services (jobs, events, blog) go to ServiceOrderForm
      const currentPlans = getCurrentPlans();
      const plan = currentPlans.find(p => p.id === planId);
      setSelectedServicePlan(plan);
      setShowServiceOrder(true);
    }
  };

  const handleContactUs = () => {
    window.location.href = 'mailto:order@shoppersdiscount.club?subject=Elite/Sponsor Package Inquiry';
  };

  const getCurrentPlans = () => {
    const category = categories.find(cat => cat.id === selectedCategory);
    return category ? category.plans : [];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream/30 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-charcoal mb-4">
            Choose Your Perfect Package
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Flexible pricing plans designed to help your business grow and connect with more customers
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-6 py-3 ${
                selectedCategory === category.id 
                  ? 'bg-primary text-white' 
                  : 'border-primary text-primary hover:bg-primary hover:text-white'
              }`}
            >
              {category.icon}
              {category.name}
            </Button>
          ))}
        </div>

        {/* Setup Fee Notice for Directory */}
        {selectedCategory === 'directory' && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-center">
            <p className="text-amber-800 font-medium">
              <strong>One-time Setup Fee:</strong> £35.00 for Standard, Deluxe, and Platinum packages
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className={`grid gap-8 ${getCurrentPlans().length <= 2 ? 'md:grid-cols-2' : getCurrentPlans().length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'} max-w-7xl mx-auto`}>
          {getCurrentPlans().map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                plan.popular ? 'border-primary shadow-lg scale-105' : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-primary text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}
              
              <CardHeader className={`text-center ${plan.popular ? 'pt-12' : 'pt-6'}`}>
                <div className={`w-12 h-12 ${plan.color || 'bg-gray-500'} rounded-full flex items-center justify-center mx-auto mb-4 text-white`}>
                  {plan.icon}
                </div>
                
                <CardTitle className="text-xl font-bold text-charcoal mb-2">
                  {plan.name}
                </CardTitle>
                
                <p className="text-gray-600 text-sm mb-4">
                  {plan.description}
                </p>

                <div className="flex flex-col items-center">
                  {plan.setup && (
                    <p className="text-sm text-gray-500 mb-1">
                      Setup: {plan.setup}
                    </p>
                  )}
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-charcoal">
                      {plan.price}
                    </span>
                    <span className="text-gray-600 ml-1">
                      {plan.period}
                    </span>
                  </div>
                  
                  {plan.jobCount && (
                    <Badge variant="secondary" className="mt-2">
                      {plan.jobCount}
                    </Badge>
                  )}
                  
                  {plan.eligibility && (
                    <Badge variant="outline" className="mt-2 text-xs">
                      {plan.eligibility}
                    </Badge>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                  
                  {plan.limitations && plan.limitations.map((limitation, index) => (
                    <li key={`limitation-${index}`} className="flex items-start gap-3 opacity-60">
                      <span className="text-gray-400 flex-shrink-0 mt-0.5">•</span>
                      <span className="text-sm text-gray-500">{limitation}</span>
                    </li>
                  ))}
                </ul>

                {plan.isEnterprise ? (
                  <Button 
                    onClick={() => handleGetStarted(plan.id, selectedCategory)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Request Quote
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleGetStarted(plan.id, selectedCategory)}
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-primary hover:bg-primary/90 text-white' 
                        : 'border-primary text-primary hover:bg-primary hover:text-white'
                    }`}
                    variant={plan.popular ? "default" : "outline"}
                  >
                    Get Started
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mt-16 text-center">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-charcoal mb-4">
                Need Help Choosing?
              </h3>
              <p className="text-gray-600 mb-6">
                Our team is here to help you select the perfect package for your business needs. 
                Get personalized recommendations and answers to all your questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleContactUs}
                  className="bg-primary hover:bg-primary/90 text-white"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Sales Team
                </Button>
                <Button 
                  variant="outline" 
                  className="border-primary text-primary hover:bg-primary hover:text-white"
                  onClick={() => window.open('https://luxury-brands.shoppersdiscount.club/', '_blank')}
                >
                  View Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Comparison */}
        <div className="mt-16">
          <h3 className="text-3xl font-bold text-charcoal text-center mb-8">
            Why Choose Shoppers Discount Club?
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-charcoal mb-2">Real-Time QR Discounts</h4>
              <p className="text-gray-600">
                Engage customers with instant, location-based discounts through our innovative QR code system
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-charcoal mb-2">AI-Powered Chatbots</h4>
              <p className="text-gray-600">
                Connect with customers 24/7 through intelligent chatbots tailored to your business
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-amber rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-charcoal mb-2">Increased Footfall</h4>
              <p className="text-gray-600">
                Driving clients to physically engage with your business in order to claim their deal
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Order Modal */}
      <ServiceOrderForm
        isOpen={showServiceOrder}
        onClose={() => setShowServiceOrder(false)}
        serviceType={selectedCategory}
        selectedPlan={selectedServicePlan}
      />
    </div>
  );
};

export default PricingPage;